<template>
  <!-- Warning ⚠️ -->
  <!-- This component has some strange duplicated code. -->
  <!-- If you can't figure out why your changes aren't showing up, look for another instance of the same code. -->
   
  <div class="grid-container">
    <MeetingConnectPromptModal
      v-if="meeting && !meeting.entity"
      ref="connectPrompt"
      :meeting-id="meeting.id"
      @connected="handleConnected"
      @cancel="() => { /* future: mark seen */ }"
    />
    <!-- Fetch all meetings -->
    <LoaderWithText
      v-if="!hasFetchedMeetings || fetchingMeetingsList"
      class="stk-py-8"
    >
      Fetching List of Meetings…
    </LoaderWithText>
    <template v-else>
      <!-- We have no meetings at all -->
      <div
        v-if="meetingsList.length === 0 && !meetingId"
        id="header"
      >
        <Level class="stk-py-8">
          <LevelLeft>
            <Stack gap="2">
              <Title>
                No meeting available.
              </Title>
            </Stack>
          </LevelLeft>

          <LevelRight>
            <Buttons>
              <MeetingBotSettingsButton />

              <AddMeetingButton
                :entity-external-id="entityExternalId"
                @meeting-created="handleMeetingCreated"
              />

              <Button
                :to="{ name: 'meetings' }"
                icon-after="fas fa-arrow-right"
              >
                View All Meetings
              </Button>
            </Buttons>
          </LevelRight>
        </Level>
        <StakeModal v-model="showCrmLinkingModalPrep">
          <CrmLinkingModal
            v-if="showCrmLinkingModalPrep"
            :entity-id="entityExternalId"
            :meeting-id="meetingId"
            message="To sync the overall summary with your CRM, please link this entity to a CRM contact first."
            @contact-linked="handlePullPrepFromCrm"
            @close="showCrmLinkingModalPrep = false"
          />
        </StakeModal>
        <div class="stk-px-8 stk-py-6">
          <Stack>
            <Callout
              v-if="!hideNotification"
              icon-before="fas fa-info fa-sm"
              status="info"
            >
              This AI-generated summary includes key financial data and discussion topics for {{ entity?.name || 'this contact' }} from your CRM.
            </Callout>
            <button
              v-if="isCrmConnected && selectedCrm"
              class="stk-button is-primary stk-size-3 stk-ml-2"
              :disabled="isPullingMeetingPrep || !isCrmConnected"
              @click="handlePullPrepFromCrm"
            >
              <i
                :class="[
                  'fas fa-sync',
                  { 'fa-spin': isPullingMeetingPrep }
                ]"
              />
              <span>Pull Meeting Prep From CRM</span>
            </button>
            <template>
              <Stack
                v-if="meetingPrimer"
                gap="4"
              >
                <!-- Summary Section -->
                <Title>
                  Pre-Meeting Overall Summary <i>{{ crmContact ? 'for ' + crmContact.name : '' }}</i>
                </Title>

                <div class="tw-flex tw-items-center tw-justify-between">
                  <Title size="5">
                    General Summary
                  </Title>

                  <Button
                    :disabled="isUpdatingPrimer || isGenerating"
                    :icon-before="editingPrimerSummary ? 'fas fa-save' : 'fas fa-edit'"
                    size="3"
                    @click="handlePrimerSummaryUpdate"
                  >
                    <span>{{ editingPrimerSummary ? 'Save' : 'Edit' }}</span>
                  </Button>
                </div>

                <textarea
                  v-if="editingPrimerSummary"
                  v-model="editedPrimer.summary"
                  class="textarea"
                  rows="6"
                />

                <p v-else>
                  {{ meetingPrimer?.summary }}
                </p>

                <!-- Questions Section -->
                <div class="tw-flex tw-items-center tw-justify-between">
                  <Title size="5">
                    Suggested Questions
                  </Title>

                  <Button
                    :disabled="isUpdatingPrimer || isGenerating"
                    :icon-before="editingPrimerQuestions ? 'fas fa-save' : 'fas fa-edit'"
                    size="3"
                    @click="editingPrimerQuestions = !editingPrimerQuestions"
                  >
                    <span>{{ editingPrimerQuestions ? 'Save' : 'Edit' }}</span>
                  </Button>
                </div>

                <EditableList
                  :items="meetingPrimer?.suggested_questions"
                  :editable="editingPrimerQuestions"
                  @update="handlePrimerQuestionsUpdate"
                />

                <!-- Financial Summary Section -->
                <div class="tw-flex tw-items-center tw-justify-between">
                  <Title size="5">
                    Financial Summary
                  </Title>

                  <Button
                    :disabled="isUpdatingPrimer || isGenerating"
                    :icon-before="editingPrimerFinancial ? 'fas fa-save' : 'fas fa-edit'"
                    size="3"
                    @click="editingPrimerFinancial = !editingPrimerFinancial"
                  >
                    <span>{{ editingPrimerFinancial ? 'Save' : 'Edit' }}</span>
                  </Button>
                </div>

                <div>
                  <EditableKeyValue
                    :store="meetingPrimer?.financial_summary"
                    :editable="editingPrimerFinancial"
                    @update="handlePrimerFinancialUpdate"
                  />
                </div>
              </Stack>

              <LoaderWithText
                v-if="isGenerating"
              >
                Fetching meeting primer… This may take a few minutes.
              </LoaderWithText>
            </template>
          </Stack>
        </div>
      </div>

      <!-- We have some, so let's fetch one -->
      <template v-else>
        <LoaderWithText
          v-if="isInitializingData || fetchingSingleMeeting"
          class="stk-py-8"
        >
          Fetching Meeting…
        </LoaderWithText>

        <template v-else>
          <div id="header">
            <div class="stk-py-8 tw-flex tw-items-center">
              <div class="tw-grow">
                <template v-if="meeting">
                  <!-- TODO: Meetings don't have titles in the models -- nor do they have sources or durations... -->
                  <Title size="8">
                    <input
                      v-model="editedMeeting.title"
                      type="text"
                      maxlength="100"
                      class="stk-title stk-size-8 tw-border-none tw-w-full transparent-input"
                      placeholder="Enter a meeting title"
                      @blur="saveMeetingChanges"
                    >
                  </Title>
                  <Subtitle size="6">
                    <input
                      v-model="editedMeeting.meeting_date"
                      type="date"
                      class="stk-subtitle stk-size-6 tw-border-none transparent-input"
                      @blur="saveMeetingChanges"
                    >
                    <br>
                    {{ meeting.source || '' }}<br v-if="meeting.source">
                    {{ meeting.duration || '' }}
                  </Subtitle>
                  <Subtitle size="6">
                    {{ crmContact?.name || '' }}
                  </Subtitle>
                </template>

                <template v-else>
                  <Title size="8">
                    No Meeting Selected
                  </Title>
                  <Subtitle size="6">
                    Browse your meetings or import a new one to get started
                  </Subtitle>
                </template>
              </div>

              <div>
                <Buttons>
                  <MeetingBotSettingsButton />

                  <Button
                    v-if="meeting && !meeting.entity && isOwned"
                    size="3"
                    @click="$refs.connectPrompt && $refs.connectPrompt.open()"
                  >
                    <span>Connect to person</span>
                  </Button>

                  <Button
                    v-if="entityExternalId"
                    @click="showMeetingsListModal = true"
                  >
                    <span>Browse Meetings</span>
                  </Button>

                  <Button
                    v-else
                    :to="{ name: 'meetings' }"
                  >
                    <span>View All Meetings</span>
                  </Button>

                  <AddMeetingButton
                    :entity-external-id="entityExternalId"
                    @meeting-created="handleMeetingCreated"
                  />
                </Buttons>
              </div>
            </div>
          </div>

          <template v-if="meeting">
            <Callout
              id="type"
              class="stk-mb-8"
              status="primary"
            >
              <Title size="4_5">
                Meeting Type
              </Title>

              <div
                class="dropdown"
                :class="{ 'is-active': showMeetingTypeDropdown }"
              >
                <div class="dropdown-trigger">
                  <Button
                    v-if="isReprocessing"
                    spacing="tight"
                    :disabled="true"
                    icon-before="fas fa-spinner fa-spin"
                  >
                    Reprocessing (3-7 mins)…
                  </Button>

                  <Button
                    v-else
                    spacing="tight"
                    icon-after="fas fa-angle-down"
                    @click="showMeetingTypeDropdown = !showMeetingTypeDropdown"
                  >
                    {{ meetingTypes.find(t => t.value === meeting?.meeting_type)?.label || 'Client Meeting' }}
                  </Button>
                </div>

                <div
                  class="dropdown-menu"
                  role="menu"
                >
                  <div class="dropdown-content">
                    <a
                      v-for="type in meetingTypes"
                      :key="type.value"
                      class="dropdown-item"
                      :disabled="type.value === meeting?.meeting_type"
                      @click="reprocessMeeting(type.value)"
                    >
                      {{ type.value === meeting?.meeting_type ?
                        type.label :
                        `Reprocess as ${type.label}` }}
                    </a>
                  </div>
                </div>
              </div>

              <p>Meeting types help accurately process the summary and next steps</p>
            </Callout>

            <Stack id="main-content">
              <Pills>
                <PillsList>
                  <PillItem
                    v-if="entityExternalId"
                    :active="activeTab === 'prep'"
                    icon="fas fa-sticky-note"
                    @click="activeTab = 'prep'"
                  >
                    Pre-Meeting Prep
                  </PillItem>
                  <PillItem
                    :active="activeTab === 'summary'"
                    icon="fas fa-clipboard-list"
                    @click="activeTab = 'summary'"
                  >
                    Meeting Summary
                  </PillItem>
                  <PillItem
                    :active="activeTab === 'email'"
                    icon="fas fa-share"
                    @click="activeTab = 'email'"
                  >
                    Follow-up Email
                  </PillItem>
                  <PillItem
                    :active="activeTab === 'transcript'"
                    icon="fas fa-align-left"
                    @click="activeTab = 'transcript'"
                  >
                    Transcript
                  </PillItem>
                </PillsList>
              </Pills>

              <div class="stk-px-8 stk-py-6">
                <Stack v-if="activeTab === 'prep'">
                  <Callout
                    v-if="!hideNotification"
                    icon-before="fas fa-info fa-sm"
                    status="info"
                  >
                    This AI-generated summary includes key financial data and discussion topics for {{ entity?.name || 'this contact' }} from your CRM.
                  </Callout>

                  <template>
                    <Menu>
                      <MenuLabel>
                        Meeting Prep
                      </MenuLabel>

                      <div
                        class="dropdown"
                        :class="{ 'is-active': isMeetingPrepDropdownActive }"
                      >
                        <div class="dropdown-trigger">
                          <button
                            class="stk-button stk-size-3"
                            :disabled="isPullingMeetingPrep || !isCrmConnected"
                            aria-haspopup="true"
                            aria-controls="dropdown-menu"
                            @click="isMeetingPrepDropdownActive = !isMeetingPrepDropdownActive"
                          >
                            <span>{{ selectedCrm || 'Select a CRM' }}</span>
                            <span class="stk-icon">
                              <i
                                class="fas fa-angle-down"
                                aria-hidden="true"
                              />
                            </span>
                          </button>
                        </div>

                        <div
                          v-if="isCrmConnected"
                          id="dropdown-menu"
                          class="dropdown-menu"
                          role="menu"
                        >
                          <div class="dropdown-content">
                            <a
                              v-for="crm in connectedCrms"
                              :key="crm"
                              href="#"
                              class="dropdown-item"
                              @click="selectCRM(crm)"
                            >
                              {{ crm }}
                            </a>
                          </div>
                        </div>
                        <a
                          v-else
                          href="/ui/advisor/integrations/"
                          class="stk-button is-primary stk-size-3"
                        >
                          Connect CRM
                        </a>

                        <StakeTooltip :label="crmSharingMessage" :active="!isOwned">
                          <button
                            v-if="isCrmConnected && selectedCrm"
                            class="stk-button is-primary stk-size-3 stk-ml-2"
                            :disabled="isPullingMeetingPrep || !isCrmConnected || !isOwned"
                            @click="handlePullPrepFromCrm"
                          >
                            <i
                              :class="[
                                'fas fa-sync',
                                { 'fa-spin': isPullingMeetingPrep }
                              ]"
                            />
                            <span>Pull Meeting Prep From CRM</span>
                          </button>
                        </StakeTooltip>
                      </div>
                    </Menu>

                    <Stack
                      v-if="meetingPrimer"
                      gap="4"
                    >
                      <!-- Summary Section -->
                      <Title>
                        Pre-Meeting Overall Summary <i>{{ crmContact ? 'for ' + crmContact.name : '' }}</i>
                      </Title>

                      <div class="tw-flex tw-items-center tw-justify-between">
                        <Title size="5">
                          General Summary
                        </Title>

                        <Button
                          :disabled="isUpdatingPrimer || isGenerating"
                          :icon-before="editingPrimerSummary ? 'fas fa-save' : 'fas fa-edit'"
                          size="3"
                          @click="handlePrimerSummaryUpdate"
                        >
                          <span>{{ editingPrimerSummary ? 'Save' : 'Edit' }}</span>
                        </Button>
                      </div>

                      <textarea
                        v-if="editingPrimerSummary"
                        v-model="editedPrimer.summary"
                        class="textarea"
                        rows="6"
                      />

                      <p v-else>
                        {{ meetingPrimer?.summary }}
                      </p>

                      <!-- Questions Section -->
                      <div class="tw-flex tw-items-center tw-justify-between">
                        <Title size="5">
                          Suggested Questions
                        </Title>

                        <Button
                          :disabled="isUpdatingPrimer || isGenerating"
                          :icon-before="editingPrimerQuestions ? 'fas fa-save' : 'fas fa-edit'"
                          size="3"
                          @click="editingPrimerQuestions = !editingPrimerQuestions"
                        >
                          <span>{{ editingPrimerQuestions ? 'Save' : 'Edit' }}</span>
                        </Button>
                      </div>

                      <EditableList
                        :items="meetingPrimer?.suggested_questions"
                        :editable="editingPrimerQuestions"
                        @update="handlePrimerQuestionsUpdate"
                      />

                      <!-- Financial Summary Section -->
                      <div class="tw-flex tw-items-center tw-justify-between">
                        <Title size="5">
                          Financial Summary
                        </Title>

                        <Button
                          :disabled="isUpdatingPrimer || isGenerating"
                          :icon-before="editingPrimerFinancial ? 'fas fa-save' : 'fas fa-edit'"
                          size="3"
                          @click="editingPrimerFinancial = !editingPrimerFinancial"
                        >
                          <span>{{ editingPrimerFinancial ? 'Save' : 'Edit' }}</span>
                        </Button>
                      </div>

                      <div>
                        <EditableKeyValue
                          :store="meetingPrimer?.financial_summary"
                          :editable="editingPrimerFinancial"
                          @update="handlePrimerFinancialUpdate"
                        />
                      </div>
                    </Stack>

                    <LoaderWithText
                      v-if="isGenerating"
                    >
                      Fetching meeting primer… This may take a few minutes.
                    </LoaderWithText>
                  </template>
                </Stack>

                <Stack
                  v-if="activeTab === 'summary'"
                  gap="4"
                >
                  <Level>
                    <LevelLeft>
                      <Title>
                        General Summary
                      </Title>
                    </LevelLeft>

                    <LevelRight>
                      <Button
                        :icon-before="editingSummary ? 'fas fa-save' : 'fas fa-edit'"
                        size="3"
                        @click="handleSummaryEdit"
                        :disabled="!canEdit"
                      >
                        <span>{{ editingSummary ? 'Save' : 'Edit' }}</span>
                      </Button>

                      <Button
                        size="3"
                        :disabled="isReprocessingSummary"
                        @click="handleReprocessSummary"
                      >
                        <span>{{ isReprocessingSummary ? 'Reprocessing...' : 'Regenerate Summary' }}</span>
                      </Button>

                      <div
                        class="dropdown"
                        :class="{ 'is-active': showPushSummaryDropdown }"
                      >
                        <div class="dropdown-trigger">
                          <button
                            class="stk-button stk-size-3"
                            :disabled="isPushingSummary || !isCrmConnected"
                            aria-haspopup="true"
                            aria-controls="dropdown-menu"
                            @click="showPushSummaryDropdown = !showPushSummaryDropdown"
                          >
                            <span>{{ selectedCrm || 'Select a CRM' }}</span>
                            <span class="stk-icon">
                              <i
                                class="fas fa-angle-down"
                                aria-hidden="true"
                              />
                            </span>
                          </button>
                        </div>

                        <div
                          v-if="isCrmConnected"
                          class="dropdown-menu"
                          role="menu"
                        >
                          <!-- Add dropdown for CRM selection -->
                          <div class="dropdown-content">
                            <a
                              v-for="crm in connectedCrms"
                              :key="crm"
                              href="#"
                              class="dropdown-item"
                              @click="selectCRM(crm)"
                            >
                              {{ crm }}
                            </a>
                          </div>
                        </div>
                        <a
                          v-else
                          href="/ui/advisor/integrations/"
                          class="stk-button is-primary stk-size-3"
                        >
                          Connect CRM
                        </a>
                        <div class="tw-flex tw-flex-col tw-gap-2 stk-ml-2">
                          <StakeTooltip :label="crmSharingMessage" :active="!isOwned">
                            <Button
                              v-if="isCrmConnected && selectedCrm"
                              class="stk-button is-primary stk-size-3 "
                              :disabled="isPushingSummary || !isCrmConnected || !meeting?.summary || !isOwned"
                              @click="handlePushSummaryToCrm"
                            >
                              <i
                                :class="[
                                  'fas fa-sync',
                                  { 'fa-spin': isPushingSummary }
                                ]"
                              />
                              <span>Push Summary to CRM</span>
                            </Button>
                          </StakeTooltip>
                          <div
                            v-if="meeting?.summary_pushed_to_crm"
                            class="tw-text-sm tw-text-gray-500"
                          >
                            Already Pushed
                          </div>
                        </div>
                      </div>
                    </LevelRight>
                  </Level>

                  <div>
                    <textarea
                      v-if="editingSummary"
                      v-model="editedMeeting.summary"
                      class="textarea"
                      rows="6"
                    />
                    <div
                      v-else
                      class="content"
                      v-html="renderMarkdown(editedMeeting?.summary)"
                    />
                  </div>

                  <Level>
                    <LevelLeft>
                      <Title>
                        Key Information
                      </Title>
                    </LevelLeft>

                    <LevelRight>
                      <Button
                        :icon-before="editingKeyInfo ? 'fas fa-save' : 'fas fa-edit'"
                        size="3"
                        :disabled="isSavingEdits || !canEdit"
                        @click="handleKeyInfoEdit"
                      >
                        <span>{{ editingKeyInfo ? 'Save' : 'Edit' }}</span>
                      </Button>

                      <Button
                        size="3"
                        :disabled="isReprocessingKeyInfo"
                        @click="handleReprocessKeyInfo"
                      >
                        <span>{{ isReprocessingKeyInfo ? 'Reprocessing...' : 'Regenerate Key Information' }}</span>
                      </Button>
                    </LevelRight>
                  </Level>

                  <KeyInfoTable
                    :data="editedMeeting.key_info"
                    :editable="editingKeyInfo"
                    @update="handleKeyInfoUpdate"
                  />
                </Stack>

                <Stack
                  v-if="activeTab === 'email'"
                  gap="4"
                >
                  <Level>
                    <LevelLeft>
                      <Title>
                        Follow-up Email
                      </Title>
                    </LevelLeft>

                    <LevelRight>
                      <Button
                        icon-before="fas fa-copy"
                        size="3"
                        @click="copyMarkdownAsRichText"
                      >
                        <span>Copy Email</span>
                      </Button>

                      <Button
                        size="3"
                        :disabled="isReprocessingEmail"
                        @click="handleReprocessEmail"
                      >
                        <span>{{ isReprocessingEmail ? 'Reprocessing...' : 'Regenerate Email' }}</span>
                      </Button>
                    </LevelRight>
                  </Level>

                  <div class="content">
                    <div
                      ref="emailContent"
                      v-html="renderMarkdown(meeting.followup_email)"
                    />
                  </div>
                </Stack>

                <Stack
                  v-if="activeTab === 'transcript'"
                  gap="4"
                >
                  <Level>
                    <LevelLeft>
                      <Title>
                        Meeting Transcript
                      </Title>
                    </LevelLeft>

                    <LevelRight>
                      <Buttons gap="2">
                        <Button
                          :icon-before="editingTranscript ? 'fas fa-save' : 'fas fa-edit'"
                          size="3"
                          @click="handleTranscriptEdit"
                          :disabled="!canEdit"
                        >
                          <span>{{ editingTranscript ? 'Save' : 'Edit' }}</span>
                        </Button>

                        <Button
                          icon-before="fas fa-copy"
                          size="3"
                          @click="copyTranscript"
                        >
                          <span>Copy</span>
                        </Button>
                      </Buttons>
                    </LevelRight>
                  </Level>

                  <div
                    ref="transcriptContent"
                    class="tw-whitespace-pre-line tw-leading-8 tw-text-gray-800"
                  >
                    <textarea
                      v-if="editingTranscript"
                      v-model="editedMeeting.transcript"
                      class="textarea"
                      rows="12"
                    />
                    <div v-else>
                      {{ editedMeeting.transcript || 'No transcript available' }}
                    </div>
                  </div>
                </Stack>
              </div>
            </Stack>

            <div id="sidebar">
              <Stack>
                <div v-if="meeting?.playable_url">
                  <audio
                    v-if="meeting.recording_type?.includes('audio')"
                    controls
                  >
                    <source
                      :src="meeting.playable_url"
                      :type="meeting.recording_type"
                    >
                    Your browser does not support the audio element.
                  </audio>

                  <video
                    v-else
                    controls
                    :src="meeting.playable_url"
                    width="100%"
                    height="auto"
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>

                <Buttons size="3_5">
                  <Button
                    v-if="meeting?.playable_url"
                    :href="meeting.playable_url"
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="primary"
                    icon-before="fas fa-play"
                    icon-after="fas fa-external-link-alt"
                    title="Click to play in a new tab"
                  >
                    <span>Playable Link</span>
                  </Button>

                  <Button
                    v-if="meeting?.download_url"
                    :href="meeting.download_url"
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="secondary"
                    icon-after="fas fa-download"
                    title="Click to download"
                  >
                    <span>Download</span>
                  </Button>

                  <Button
                    v-if="meeting?.snapshot_url"
                    :href="meeting.snapshot_url"
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="secondary"
                    icon-before="fas fa-external-link-alt"
                    title="Share this meeting"
                  >
                    <span>Share Meeting</span>
                  </Button>
                </Buttons>

                <Menu v-if="meeting">
                  <MenuLabel>Meeting Attendees</MenuLabel>

                  <Buttons>
                    <Button
                      v-for="(attendee, index) in meeting.attendees"
                      :key="index"
                      icon-after="fas fa-edit"
                      size="3"
                      @click="openAttendeeEditModal(attendee)"
                    >
                      {{ attendee }}
                    </Button>
                  </Buttons>
                </Menu>

                <Menu>
                  <Level>
                    <LevelLeft>
                      <MenuLabel>
                        Tasks
                      </MenuLabel>
                    </LevelLeft>

                    <LevelRight>
                      <Button
                        size="3"
                        :disabled="isReprocessingTasks"
                        @click="handleReprocessTasks"
                      >
                        <span>{{ isReprocessingTasks ? 'Reprocessing...' : 'Regenerate Tasks' }}</span>
                      </Button>
                    </LevelRight>
                  </Level>

                  <div
                    class="dropdown"
                    :class="{ 'is-active': isDropdownActive }"
                  >
                    <div class="dropdown-trigger">
                      <button
                        class="stk-button stk-size-3"
                        :disabled="isPushingTasks || !isCrmConnected"
                        aria-haspopup="true"
                        aria-controls="dropdown-menu"
                        @click="isDropdownActive = !isDropdownActive"
                      >
                        <span>{{ selectedCrm || 'Select a CRM' }}</span>
                        <span class="stk-icon">
                          <i
                            class="fas fa-angle-down"
                            aria-hidden="true"
                          />
                        </span>
                      </button>
                    </div>

                    <div
                      v-if="isCrmConnected"
                      id="dropdown-menu"
                      class="dropdown-menu"
                      role="menu"
                    >
                      <div class="dropdown-content">
                        <a
                          v-for="crm in connectedCrms"
                          :key="crm"
                          href="#"
                          class="dropdown-item"
                          @click="selectCRM(crm)"
                        >
                          {{ crm }}
                        </a>
                      </div>
                    </div>
                    <a
                      v-else
                      href="/ui/advisor/integrations/"
                      class="stk-button is-primary stk-size-3"
                    >
                      Connect CRM
                    </a>
                    <div class="tw-flex tw-flex-col tw-gap-2 stk-ml-2">
                      <StakeTooltip :label="crmSharingMessage" :active="!isOwned">
                        <button
                          v-if="isCrmConnected && selectedCrm"
                          class="stk-button is-primary stk-size-3"
                          :disabled="isPushingTasks || !isCrmConnected || !isOwned"
                          @click="handlePushTasksToCrm"
                        >
                          <i
                            :class="[
                              'fas fa-sync',
                              { 'fa-spin': isPushingTasks }
                            ]"
                          />
                          <span>Push Tasks to CRM</span>
                        </button>
                      </StakeTooltip>
                      <div
                        v-if="meeting?.crm_tasks?.some(task => task.pushed_to_crm)"
                        class="tw-text-sm tw-text-gray-500"
                      >
                        {{ meeting.crm_tasks.filter(task => task.pushed_to_crm).length }} Tasks Already Pushed
                      </div>
                    </div>
                  </div>
                </Menu>

                <TaskManager
                  :meeting="meeting"
                  :crm="selectedCrm"
                  :last-task-push="lastTaskPush"
                  :can-edit="canEdit"
                />

                <!-- Original Version
                <div>
                  <TaskCard
                    v-for="task in tasks"
                    :key="task.id"
                    :task="task"
                    class="tw-mb-4"
                    @click.native="openTaskEditor(task)"
                  />
                </div>
                -->
              </Stack>
            </div>
          </template>

          <!-- Attendee Edit Modal -->
          <o-modal
            :active.sync="isAttendeeEditModalOpen"
            :width="400"
            scroll="keep"
          >
            <Stack
              class="stk-p-8"
              gap="4"
              style="width: 400px;"
            >
              <Title>
                Edit Attendee Name
              </Title>

              <div>
                <Label>Name</Label>
                <o-input
                  v-model="editingAttendeeName"
                  :loading="updatingAttendeeName"
                  expanded
                />
              </div>

              <Buttons size="3">
                <Button
                  variant="primary"
                  :loading="updatingAttendeeName"
                  @click="saveAttendeeName"
                >
                  Save
                </Button>
                <Button @click="isAttendeeEditModalOpen = false">
                  Cancel
                </Button>
              </Buttons>
            </Stack>
          </o-modal>

          <!-- Task Editor Modal -->
          <o-modal
            :active.sync="isTaskEditorOpen"
            :width="1000"
            scroll="keep"
            custom-class="task-editor-modal"
          >
            <Stack
              class="stk-p-8"
              gap="4"
              style="width: 800px;"
            >
              <Title>
                Edit Task
              </Title>

              <Field>
                <Label>Title</Label>

                <o-input
                  v-model="editingTask.name"
                  expanded
                  class="tw-text-lg"
                />
              </Field>

              <Field>
                <Label>Description</Label>

                <o-input
                  v-model="editingTask.description"
                  type="textarea"
                  rows="4"
                  expanded
                />
              </Field>

              <Field>
                <Label>Status</Label>

                <o-select
                  v-model="editingTask.status"
                  placeholder="Select a status"
                  expanded
                >
                  <option value="todo">
                    To Do
                  </option>
                  <option value="in_progress">
                    In Progress
                  </option>
                  <option value="done">
                    Done
                  </option>
                </o-select>
              </Field>

              <Field>
                <Label>Assign to</Label>

                <o-select
                  v-model="editingTask.assignee"
                  placeholder="Select a team member"
                  expanded
                >
                  <option
                    v-for="member in teamMembers"
                    :key="member"
                    :value="member"
                  >
                    {{ member }}
                  </option>
                </o-select>
              </Field>

              <Field>
                <Label>Due Date</Label>

                <o-datepicker
                  v-model="editingTask.due_date"
                  placeholder="Select a due date"
                  position="top-right"
                  expanded
                />
              </Field>

              <Buttons class="tw-flex tw-justify-between">
                <div>
                  <Button
                    variant="primary"
                    @click="saveTask"
                  >
                    Save
                  </Button>
                  <Button
                    @click="closeTaskEditor"
                  >
                    Cancel
                  </Button>
                </div>

                <Button
                  variant="danger"
                  @click="deleteTask"
                >
                  Delete
                </Button>
              </Buttons>
            </Stack>
          </o-modal>

          <!-- Add CrmLinkingModal -->
          <StakeModal v-model="showCrmLinkingModalTasks">
            <CrmLinkingModal
              v-if="showCrmLinkingModalTasks"
              :entity-id="meeting?.entity?.external_id"
              :meeting-id="meetingId"
              message="To sync tasks with your CRM, please link this entity to a CRM contact first."
              @contact-linked="handlePushTasksToCrm"
              @close="showCrmLinkingModalTasks = false"
            />
          </StakeModal>
          <StakeModal v-model="showCrmLinkingModalSummary">
            <CrmLinkingModal
              v-if="showCrmLinkingModalSummary"
              :entity-id="entityExternalId"
              :meeting-id="meetingId"
              message="To sync the overall summary with your CRM, please link this entity to a CRM contact first."
              @contact-linked="handlePushSummaryToCrm"
              @close="showCrmLinkingModalSummary = false"
            />
          </StakeModal>

          <StakeModal v-model="showCrmLinkingModalPrep">
            <CrmLinkingModal
              v-if="showCrmLinkingModalPrep"
              :entity-id="entityExternalId"
              :meeting-id="meetingId"
              message="To sync the overall summary with your CRM, please link this entity to a CRM contact first."
              @contact-linked="handlePullPrepFromCrm"
              @close="showCrmLinkingModalPrep = false"
            />
          </StakeModal>

          <StakeModal
            v-model="showMeetingsListModal"
          >
            <MeetingsListModal
              v-if="showMeetingsListModal"
              :entity-external-id="entityExternalId"
              :entity-name="entity?.name"
              :meetings="meetingsList"
              :has-fetched-meetings="hasFetchedMeetings"
              @close="handleMeetingsListModalClose"
            />
          </StakeModal>

          <!-- Add this new modal -->
          <o-modal
            :active.sync="showNoEntityModal"
            :width="400"
            scroll="keep"
          >
            <div class="box">
              <h3 class="title is-5 mb-4">
                You Need to Link This Meeting to a YourStake Client or Prospect first.
              </h3>
              <p class="mb-4">
                Please go to the Meeting Assistant page and click "Link Entity" for this meeting first.
              </p>
              <div class="buttons is-right">
                <o-button @click="showNoEntityModal = false">
                  Close
                </o-button>
              </div>
            </div>
          </o-modal>

          <!-- Custom Prompt Selection Modal -->
          <o-modal
            :active.sync="showCustomPromptModal"
            :width="600"
            scroll="keep"
          >
            <div class="box">
              <h3 class="title is-5 mb-4">
                Edit {{ getSectionTitle(customPromptModalSection) }} Prompt
              </h3>
              <p class="mb-4">
                Edit the prompt used for reprocessing this section:
              </p>

              <div class="field">
                <div class="tw-flex tw-justify-between tw-items-center tw-mb-2">
                  <label class="label tw-mb-0">
                    {{ getSectionTitle(customPromptModalSection) }} Prompt
                  </label>
                  <div class="tw-flex tw-gap-2">
                    <button
                      v-if="currentPromptIsCustom"
                      class="tw-text-sm tw-px-3 tw-py-1 tw-bg-yellow-100 tw-border-transparent tw-text-yellow-800 tw-rounded"
                      @click="revertToDefaultPrompt"
                    >
                      Revert to Default
                    </button>
                    <button
                      class="tw-text-sm tw-px-3 tw-py-1 tw-bg-blue-100 tw-border-transparent tw-text-blue-800 tw-rounded"
                      @click="toggleEditPrompt"
                    >
                      {{ isEditingPrompt ? 'Cancel' : 'Edit' }}
                    </button>
                  </div>
                </div>

                <div v-if="isEditingPrompt">
                  <textarea
                    v-model="editedPromptText"
                    class="textarea"
                    rows="8"
                    placeholder="Enter your custom prompt..."
                  />
                  <div class="tw-flex tw-gap-2 tw-mt-2">
                    <button
                      class="tw-px-4 tw-py-2 tw-bg-gray-400 tw-text-white tw-border-transparent tw-rounded tw-text-sm"
                      @click="cancelEditPrompt"
                    >
                      Cancel
                    </button>
                    <button
                      class="tw-px-4 tw-py-2 tw-bg-green-600 tw-text-white tw-border-transparent tw-rounded tw-text-sm"
                      :disabled="savingPrompt || !editedPromptText.trim()"
                      @click="savePrompt"
                    >
                      {{ savingPrompt ? 'Saving...' : 'Save' }}
                    </button>
                  </div>
                </div>

                <div v-else>
                  <div class="tw-bg-gray-50 tw-p-3 tw-rounded tw-text-sm tw-max-h-40 tw-overflow-auto tw-whitespace-pre-wrap tw-break-words">
                    {{ currentPromptText }}
                  </div>
                  <div class="tw-mt-2 tw-text-xs tw-text-gray-500">
                    {{ currentPromptIsCustom ? 'Custom prompt' : 'Default prompt' }}
                  </div>
                </div>
              </div>

              <div class="buttons is-right mt-4 tw-flex gap-1">
                <o-button @click="cancelCustomPromptReprocess">
                  Cancel
                </o-button>
                <o-button
                  variant="primary"
                  class="tw-ml-4"
                  @click="confirmCustomPromptReprocess"
                >
                  Reprocess with Current Prompt
                </o-button>
              </div>
            </div>
          </o-modal>
        </template>
      </template>
    </template>
  </div>
</template>

<script>
// Warning ⚠️
// This component has some strange duplicated code.
// If you can't figure out why your changes aren't showing up, look for another instance of the same code.

import MeetingBotSettingsButton from '@frontend/ui/features/meetings/components/MeetingBotSettingsButton.vue';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore';
import * as Sentry from '@sentry/vue';
import StakeModal from '@stakeui/StakeModal.vue';
import StakeTooltip from '@stakeui/StakeTooltip.vue';
import MeetingConnectPromptModal from '@frontend/ui/features/meetings/components/MeetingConnectPromptModal.vue';
import { mapStores } from 'pinia';
import { awaitTask } from '../../checkup/helpers/tasks';
import { fetchWrapper } from '@frontend/shared/fetch-wrapper.js';
import AddMeetingButton from '../../meetings/components/AddMeetingButton.vue';
import CrmLinkingModal from '../components/CrmLinkingModal.vue';
import EditableKeyValue from '../components/EditableKeyValue.vue';
import EditableList from '../components/EditableList.vue';
import KeyInfoTable from '../components/KeyInfoTable.vue';
import MeetingsListModal from '../components/MeetingsListModal.vue';
import TaskCard from '../components/TaskCard.vue';
import TaskManager from '@features/tasks/components/TaskManager.vue';

import { marked } from 'marked';
import DOMPurify from 'dompurify';

import {
  Button,
  Buttons,
  Callout,
  Field,
  Icon,
  Label,
  Level,
  LevelLeft,
  LevelRight,
  LoaderWithText,
  Menu,
  MenuItem,
  MenuLabel,
  MenuList,
  PillItem,
  Pills,
  PillsList,
  Stack,
  Subtitle,
  TextInput,
  Title,
} from '@stakeui';

// Configure marked
marked.setOptions({
  headerIds: false,
  mangle: false,
  breaks: true,
});

export default {
  name: 'NoteTaking',
  components: {
    // TaskCard,
    TaskManager,
    KeyInfoTable,
    EditableList,
    EditableKeyValue,
    CrmLinkingModal,
    MeetingsListModal,
    AddMeetingButton,
    MeetingBotSettingsButton,
    StakeModal,
    StakeTooltip,
    MeetingConnectPromptModal,
    Button,
    Buttons,
    Callout,
    Field,
    Icon,
    Label,
    Level,
    LevelLeft,
    LevelRight,
    LoaderWithText,
    Menu,
    // MenuItem,
    MenuLabel,
    // MenuList,
    PillItem,
    Pills,
    PillsList,
    Stack,
    Subtitle,
    // TextInput,
    Title,
  },
  props: {
    entityExternalId: {
      type: String,
      default: '',
    },
    meetingIdQueryParam: {
      type: [String, Number],
      required: false,
      default: null,
    },
  },
  data() {
    return {
      meetingId: this.meetingIdQueryParam,
      meetingsList: [],
      fetchingMeetingsList: false,
      hasFetchedMeetings: false,
      meeting: null,
      fetchingSingleMeeting: false,
      isInitializingData: false,
      tasks: [],
      lastTaskPush: null,
      entity: null,
      activeTab: 'summary',
      showCRMDropdown: false,
      hasLinkedCRM: false,
      crmUsers: {},
      crmContact: null,
      teamMembers: [],
      connectedCrms: [],
      isTaskEditorOpen: false,
      editingTask: {
        id: null,
        name: '',
        description: '',
        status: '',
        assignees: [],
        assignee: '',
        due_date: null,
      },
      isPushingTasks: false,
      isPullingMeetingPrep: false,
      isPushingSummary: false,
      isDropdownActive: false,
      isMeetingPrepDropdownActive: false,
      showCrmLinkingModalTasks: false,
      showCrmLinkingModalSummary: false,
      showCrmLinkingModalPrep: false,
      selectedCrm: null,
      entityCrmType: null,
      showMeetingsListModal: false,
      editedPrimer: {
        summary: '',
        suggested_questions: [],
        financial_summary: {},
      },
      isUpdatingPrimer: false,
      editingPrimerSummary: false,
      editingPrimerQuestions: false,
      editingPrimerFinancial: false,
      isGenerating: false,
      hideNotification: false,
      isBrowseDropdownActive: false,
      isSavingEdits: false,
      editingSummary: false,
      editingKeyInfo: false,
      editingTranscript: false,
      editedMeeting: {
        summary: '',
        key_info: [],
        transcript: '',
      },
      isAttendeeEditModalOpen: false,
      editingAttendeeName: '',
      originalAttendeeName: '',
      updatingAttendeeName: false,
      showPushSummaryDropdown: false,
      showMeetingTypeDropdown: false,
      isReprocessing: false,
      isReprocessingSummary: false,
      isReprocessingKeyInfo: false,
      isReprocessingEmail: false,
      isReprocessingTasks: false,
      showCustomPromptModal: false,
      customPromptModalSection: '', // 'summary' or 'key_info'
      availableCustomPrompts: {},
      editedPromptText: '',
      isEditingPrompt: false,
      savingPrompt: false,
      meetingTypes: [
        { label: 'Client Meeting', value: 'client' },
        { label: 'Discovery/Intro Meeting', value: 'discovery' },
        { label: 'Dictation/Summary', value: 'dictation' },
        { label: 'Internal Team Meeting', value: 'internal' },
        { label: 'General Meeting', value: 'general' },
      ],
      showNoEntityModal: false,
      showConnectPrompt: false,
      contactUpdateTimeout: null,
      crmSharingMessage: "This is a shared meeting. Only the meeting owner can pull and push CRM data, but any updates done by the meeting owner will be visible to you.",
      canEdit: false
    };
  },
  watch: {
    meetingIdQueryParam(newVal, oldVal) {
      this.meetingId = newVal;
    },
    'meetingId': {
      immediate: true,
      handler(newVal, oldVal) {
        this.fetchMeetingAndInitializeTasks();
        this.showMeetingsListModal = false;
      },
    },
    'meeting': {
      immediate: true,
      async handler(newMeeting) {
        this.canEdit = false;
        
        if (newMeeting) {
          let meetingDateIso = '';
          try {
            meetingDateIso = new Date(newMeeting?.meeting_date).toISOString().slice(0, 10);
          } catch (e) {
            meetingDateIso = '';
            console.error('Invalid meeting date:', newMeeting?.meeting_date, e);
          }
          this.editedMeeting = {
            title: newMeeting.title,
            meeting_date: meetingDateIso,
            summary: newMeeting.summary,
            key_info: JSON.parse(JSON.stringify(newMeeting.key_info || [])),
            transcript: newMeeting.transcript,
          };
          this.$nextTick(() => {
            if (!newMeeting.entity && !newMeeting.connect_prompt_seen_at && this.isOwned) {
              this.showConnectPrompt = true;
              if (this.$refs.connectPrompt) this.$refs.connectPrompt.open();
            } else {
              this.showConnectPrompt = false;
            }
          });
        }
        
        this.canEdit = await this.checkCanEdit();
      },
    },
    'entity': {
      immediate: true,
      handler(newPrimer) {
        console.log('ENTITY PRIMER', newPrimer);
      },
    },
    'meetingPrimer': {
      immediate: true,
      handler(newPrimer) {
        if (newPrimer) {
          this.editedPrimer = {
            summary: newPrimer.summary,
            suggested_questions: JSON.parse(JSON.stringify(newPrimer.suggested_questions)),
            financial_summary: JSON.parse(JSON.stringify(newPrimer.financial_summary)),
          };
        }
      },
    },
    'meeting.crm_connection_id': {
      immediate: true,
      async handler(newId) {
        // this.crmContact = await this.fetchCrmContact(this.selectedCrm, newId);
        this.scheduleContactUpdate();
      },
    },
    'meeting.crm_type': {
      immediate: true,
      async handler(newCrmType) {
        console.log('IN WATCHER', newCrmType);
        this.selectedCrm = newCrmType || this.selectedCrm;
        // this.crmContact = await this.fetchCrmContact(this.selectedCrm, this.meeting?.crm_connection_id);
        this.scheduleContactUpdate();
      },
    },
  },
  async created() {
    // get connected crms (may be async)
    // get selected crm (usually just the first and only connected crm, but user may change it)
    // get crm users for selected crm
    // assign tasks to crm users
    // fetch meeting and entity data
    // fetch primer
    // add event listeners
    console.log('MEETING ID QUERY PARAM', this.meetingIdQueryParam);
    await this.fetchMeetingsList();
    await this.fetchEntity();
    const initializeData = async () => {
      this.isInitializingData = true;

      try {
        // Get connected CRMs from advisor store

        const res = await fetch('/api/v1/oauth/connections/', {
          method: 'GET',
          headers: this.headers,
        });
        const data = await res.json();
        let activeOAuthConnections = {};
        if (data) {
          activeOAuthConnections = data.reduce((acc, app) => {
            acc[app] = true;
            return acc;
          }, {});
        }

        this.connectedCrms = Object.keys(activeOAuthConnections).filter(appName =>
          ['Wealthbox', 'Redtail', 'Salesforce'].includes(appName),
        );

        // Set initial CRM if available
        if (this.connectedCrms.length > 0) {
          // If entity has a CRM type that matches a connected CRM, use that
          // Otherwise use first connected CRM
          this.selectedCrm = this.connectedCrms[0];
          console.log('connected CRMs', this.connectedCrms);
          // Get users for all connected CRMs
          const userPromises = this.connectedCrms.map(crm => this.getUsersFromCRM(crm));

          const userResults = await Promise.all(userPromises);
          this.crmUsers = Object.assign({}, ...userResults);
          console.log('crmUsers', this.crmUsers);
          // set team members
          this.teamMembers = (this.crmUsers[this.selectedCrm] || []).map(user => user.name);

          // Update task assignees based on selected CRM
          if (this.selectedCrm) {
            this.updateTaskAssignees(this.selectedCrm);
          }
        }
        // this.crmContact = await this.fetchCrmContact(this.meeting?.crm_type || this.selectedCrm, this.meeting?.crm_connection_id);
        this.scheduleContactUpdate();

        // Fetch meeting and entity data
        if (this.meetingId && this.meetingId !== 'new') {
          await this.fetchMeetingAndInitializeTasks();
        } else if (this.meetingsList.length > 0 && this.meetingsList[0].id) {
          this.meetingId = this.meetingsList[0].id;
        }
      } catch (error) {
        console.error('Error initializing data:', error);
        Sentry.captureException(error);
      } finally {
        this.isInitializingData = false;
      }
    };

    await initializeData();

    // Add click outside listener
    document.addEventListener('click', this.handleClickOutside);
  },
  computed: {
    ...mapStores(useAdvisorStore),
    headers() {
      return {
        'X-CSRFToken': this.$root.$data.csrfToken,
        'Content-Type': 'application/json',
      };
    },
    isCrmConnected() {
      console.log('CONNECTED CRMS', this.connectedCrms);
      return this.connectedCrms?.length > 0;
    },
    meetingPrimer() {
      console.log('MEETING PRIMER', this.meeting?.meeting_primer);
      console.log('ENTITY PRIMER', this.entity?.latest_meeting_primer);
      return this.meeting?.meeting_primer || this.entity?.latest_meeting_primer;
    },
    currentPromptText() {
      if (!this.customPromptModalSection || !this.meeting?.meeting_type) return '';

      const promptData = this.availableCustomPrompts[this.meeting.meeting_type]?.[this.customPromptModalSection];
      if (promptData && promptData.text) {
        return promptData.text;
      }

      // Fallback to default prompt text
      return this.getDefaultPromptText(this.customPromptModalSection);
    },
    currentPromptIsCustom() {
      if (!this.customPromptModalSection || !this.meeting?.meeting_type) return false;

      const promptData = this.availableCustomPrompts[this.meeting.meeting_type]?.[this.customPromptModalSection];
      return promptData && promptData.is_custom;
    },
    isOwned() {
      return this.meeting?.advisor_external_id === this.advisorStore?.advisor?.external_id
    }
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    scheduleContactUpdate() {
      if (this.contactUpdateTimeout) {
        clearTimeout(this.contactUpdateTimeout);
      }

      const crm = this.meeting?.crm_type || this.selectedCrm;
      const cid = this.meeting?.crm_connection_id;

      if (!crm || !cid) {
        this.crmContact = null;
        return;
      }

      this.contactUpdateTimeout = setTimeout(async () => {
        this.crmContact = await this.fetchCrmContact(crm, cid);
      }, 300);
    },
    async fetchCrmContact(selectedCrm, crmConnectionId) {
      if (crmConnectionId) {
        try {
          const response = await fetch(`/api/v1/crm/${selectedCrm.toLowerCase()}/contact/${crmConnectionId}/`, {
            headers: this.headers,
          });
          let data = { name: `${selectedCrm} Contact` };
          if (response.ok) data = await response.json();
          return data;
        } catch (error) {
          console.error('Error fetching CRM contact:', error);
          Sentry.captureException(error);
        }
        return null;
      }
    },
    async fetchMeetingsList() {
      this.fetchingMeetingsList = true;

      try {
        if (this.entityExternalId) {
          console.log('fetching meetings');
          const response = await fetch(`/api/v1/meetings/entity/${this.entityExternalId}/`);
          const data = await response.json();
          this.meetingsList = data.meetings;
        } else {
          this.meetingsList = [];
        }
      } catch (error) {
        console.error('Error fetching meetings:', error);
      } finally {
        this.fetchingMeetingsList = false;
        this.hasFetchedMeetings = true;
      }
    },
    async fetchMeeting() {
      this.fetchingSingleMeeting = true;
      const meetingResponse = await fetch(`/api/v1/meetings/${this.meetingId}/`);
      const meetingData = await meetingResponse.json();
      this.fetchingSingleMeeting = false;
      this.meeting = meetingData;
    },
    async fetchMeetingAndInitializeTasks() {
      if (this.meetingId && this.meetingId !== 'new') {
        await this.fetchMeeting();
        this.tasks = this.initializeTasks(this.meeting.crm_tasks);
        this.activeTab = 'summary';
        this.selectedCrm = this.meeting?.crm_type || this.selectedCrm;
        this.$nextTick(() => {
          if (this.meeting && !this.meeting.entity && !this.meeting.connect_prompt_seen_at && this.isOwned) {
            this.showConnectPrompt = true;
            if (this.$refs.connectPrompt) this.$refs.connectPrompt.open();
          }
        });
      }
    },
    handleConnected(person) {
      this.fetchMeeting();
      this.showConnectPrompt = false;
    },
    copyEmail() {
      const emailContent = this.$refs.emailContent.innerText;
      navigator.clipboard.writeText(emailContent).then(() => {
        // You can add a notification here to inform the user that the email has been copied
        alert('Email content copied to clipboard');
      }).catch(err => {
        console.error('Failed to copy email content: ', err);
      });
    },
    async copyLink(url) {
      await navigator.clipboard.writeText(url);
      this.$oruga.notification.open({
        message: 'Link copied to clipboard',
        variant: 'success',
      });
    },
    toggleCRMDropdown() {
      this.showCRMDropdown = !this.showCRMDropdown;
    },
    selectCRM(crmName) {
      this.selectedCrm = crmName;
      this.isDropdownActive = false;
      this.updateTaskAssignees(crmName);
    },
    async getUsersFromCRM(crm) {
      if (!this.connectedCrms.includes(crm)) {
        return { [crm]: [] };
      }

      const res = await fetchWrapper(`/api/v1/crm/${crm.toLowerCase()}/users/`, {
        method: 'GET',
        headers: this.headers,
      });
      const data = await res.json();

      return {
        [crm]: data.users || [],
      };

      // this.crmUsers = {
      //   ...this.crmUsers,
      //   [crm]: data.users,
      // };
      // if (data.users) {
      //   console.log(`data.users for ${crm}`, data.users);
      //   this.teamMembers = data.users.map(user => user.name);
      // }
    },
    async sendTasksToCRM() {
      const res = await fetchWrapper(`/api/v1/crm/${this.selectedCrm.toLowerCase()}/tasks/`, {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify({
          tasks: this.assignTasks(this.selectedCrm, this.tasks),
          contactId: this.meeting.crm_connection_id,
          meetingId: this.meeting.id,
        }),
      });

      if (!res.ok) {
        Sentry.captureMessage('Failed to sync tasks to CRM', {
          level: 'error',
          extra: {
            status: res.status,
            body: await res.text(),
            crm,
            contactId,
          },
        });
        alert('Failed to sync tasks to CRM');
        return;
      }

      const data = await res.json();
      console.log('Tasks synced:', data);
      await this.fetchMeeting();
      alert('Successfully pushed tasks to CRM');
      return data;
    },
    assignTasks(crm, tasks) {
      // TODO: This might not be the best way to do this, but okay until we finalize UI.
      return tasks.map(task => {
        const names = task.assignees;
        console.log('NAMES', names);
        if (!names) return task;
        console.log('CRM USERS', this.crmUsers);
        const users = this.crmUsers[crm];
        console.log('USERS', users);
        if (!users) return task;

        const ids = users.reduce((acc, user) => {
          acc[user.name] = user.id;
          return acc;
        }, {});
        console.log('IDS', ids);

        const assignedTasks = {
          ...task,
          assignees: names.map(name => {
            console.log('NAME', name);
            const id = ids[name];
            return id ? { name, id } : null;
          }).filter(Boolean),
        };
        console.log('ASSIGNED TASKS', assignedTasks);
        return assignedTasks;
      });
    },
    generateDueDate(index) {
      const today = new Date();
      const dueDate = new Date(today.setDate(today.getDate() + index + 1));
      return dueDate.toISOString().split('T')[0];
    },
    openTaskEditor(task) {
      this.editingTask = {
        ...task,
        assignee: Array.isArray(task.assignees) ? task.assignees[0] : (this.teamMembers[0] || ''),
        due_date: task.due_date ? new Date(task.due_date) : null,
      };
      this.isTaskEditorOpen = true;
    },
    closeTaskEditor() {
      this.isTaskEditorOpen = false;
      this.editingTask = {
        id: null,
        name: '',
        description: '',
        status: '',
        assignees: [],
        assignee: '',
        due_date: null,
      };
    },
    async saveTask() {
      const index = this.tasks.findIndex(t => t.id === this.editingTask.id);
      if (index !== -1) {
        console.log('EDITING TASK', this.editingTask);
        const updatedTask = {
          ...this.editingTask,
          assignees: [this.editingTask.assignee],
          due_date: this.editingTask.due_date ? this.editingTask.due_date.toISOString().split('T')[0] : null,
        };
        this.$set(this.tasks, index, updatedTask);

        // Call the method to save the task to the backend
        await this.updateTaskOnServer(updatedTask);
      }
      this.closeTaskEditor();
    },
    async updateTaskOnServer(task) {
      try {
        const response = await fetch(`/api/v1/crm-tasks/${this.meetingId}/`, {
          method: 'POST',
          headers: this.headers,
          body: JSON.stringify({ tasks: this.tasks }),
        });

        if (!response.ok) {
          throw new Error('Failed to update task');
        }

        const data = await response.json();
        console.log('Task updated successfully:', data);
      } catch (error) {
        console.error('Error updating task:', error);
        // Optionally, add error handling logic here, such as showing a notification to the user
      }
    },
    initializeTasks(nextSteps) {
      let steps = [];

      if (Array.isArray(nextSteps)) {
        steps = nextSteps.filter(Boolean);
      } else if (nextSteps) {
        steps = [nextSteps];
      }

      console.log('TEAM MEMBERS IN INITIALIZE TASKS', this.teamMembers);
      return steps.map((step, index) => ({
        id: index + 1,
        name: typeof step === 'string' ? step : step?.name || '',
        description: typeof step === 'string' ? step : step.description || '',
        due_date: this.generateDueDate(index),
        status: 'todo',
        complete: false,
        assignees: [this.teamMembers[0] || ''],
        assignee: this.teamMembers[0] || '',
      }));
    },
    updateTaskAssignees(crmName=null) {
      let members = [];
      if (crmName) {
        members = this.crmUsers?.[crmName] || [];
      }

      const assignee = members[0]?.name || '';

      const updatedTasks = this.tasks.map(task => {
        return {
          ...task,
          assignees: [assignee],
          assignee,
        };
      });

      this.tasks = updatedTasks;
    },
    async deleteTask(task) {
      this.tasks = this.tasks.filter(t => t.id !== this.editingTask.id);
      await this.updateTaskOnServer(null);
      this.closeTaskEditor();
    },
    async handlePushTasksToCrm() {
      this.isPushingTasks = true;
      try {
        // await this.fetchMeeting();
        if (!this.selectedCrm) {
          this.$oruga.notification.open({
            message: 'No CRM selected',
            variant: 'warning',
          });
          return;
        }

        // We need to refresh the meeting so the frontend knows we've linked a contact
        // However we can't use fetchMeeting() because that causes other problems (see PR #7143)
        // This is 🤮 but it works and seems like the simplest fix
        const res = await fetch(`/api/v1/meetings/${this.meetingId}/`);
        const meeting = await res.json();

        if (!meeting.crm_connection_id) {
          this.showCrmLinkingModalTasks = true;
          return;
        }
        /*
        if (this.tasks?.length < 1) {
          this.$oruga.notification.open({
            message: 'No tasks to sync',
            variant: 'warning',
          });
          return;
        } */
        // await this.sendTasksToCRM();
        this.lastTaskPush = Date.now();
      } catch (error) {
        Sentry.captureException(error);
        this.$oruga.notification.open({
          message: 'Failed to push tasks to CRM',
          variant: 'danger',
        });
      } finally {
        this.isPushingTasks = false;
      }
    },
    async handlePushSummaryToCrm() {
      this.isPushingSummary = true;
      try {
        await this.fetchMeeting();
        if (!this.selectedCrm) {
          this.$oruga.notification.open({
            message: 'No CRM selected',
            variant: 'warning',
          });
          return;
        }
        if (!this.meeting.crm_connection_id) {
          this.showCrmLinkingModalSummary = true;
          return;
        }
        if (!this.meeting.summary) {
          this.$oruga.notification.open({
            message: 'No summary to sync',
            variant: 'warning',
          });
          return;
        }
        await this.pushSummaryToCRM();
      } catch (error) {
        Sentry.captureException(error);
        this.$oruga.notification.open({
          message: 'Failed to push summary to CRM',
          variant: 'danger',
        });
      } finally {
        this.isPushingSummary = false;
      }
    },
    async fetchEntity() {
      if (!this.entityExternalId) {
        return;
      }
      const entityResponse = await fetch(`/api/v1/entities/${this.entityExternalId}/`);
      const entityData = await entityResponse.json();
      this.entity = entityData;
    },
    async handlePullPrepFromCrm() {
      this.isPullingMeetingPrep = true;
      try {
        await this.fetchEntity();
        if (!this.selectedCrm) {
          this.$oruga.notification.open({
            message: 'No CRM selected',
            variant: 'warning',
          });
          return;
        }
        console.log('ENTITY', this.entity);
        if (!this.entity?.crm_connection_id) {
          this.showCrmLinkingModalPrep = true;
          return;
        }
        await this.generatePrimer();
      } catch (error) {
        console.error('ERROR', error);
        Sentry.captureException(error);
        this.$oruga.notification.open({
          message: 'Failed to pull prep from CRM',
          variant: 'danger',
        });
      } finally {
        this.isPullingMeetingPrep = false;
      }
    },
    async handleMeetingCreated() {
      console.log('MEETING CREATED');
      await this.fetchMeetingsList();
      console.log('fetching meetings');
      // Fetch the latest meetings for this entity
      const response = await fetch(`/api/v1/meetings/entity/${this.entityExternalId}/`);
      const data = await response.json();
      console.log('MEETINGS DATA', data);
      // Set the meetingId to the most recent meeting (first in the array)
      if (data.meetings && data.meetings.length > 0) {
        this.meetingId = data.meetings[0].id; // TODO: We shouldn't be mutating input props
      }
      console.log('MEETING ID', this.meetingId);
      // Fetch the updated meeting and entity data
      await this.fetchMeetingAndInitializeTasks();

      // Switch to summary tab to show the new meeting
      this.activeTab = 'summary';
      console.log('ACTIVE TAB', this.activeTab);
    },
    async generatePrimer() {
      if (this.meetingPrimer) {
        if (!window.confirm('Are you sure you want to re-pull the data from the CRM? This will remove any edits you would have made.')) {
          return;
        }
      }

      this.isGenerating = true;
      try {
        console.log('GENERATING PRIMER');
        let primerUrl = '';
        if (this.entityExternalId) {
          primerUrl = `/api/v1/meeting-primer/create/${this.entityExternalId}/`;
        } else {
          primerUrl = `/api/v1/meeting-primer/create-via-meeting/${this.meetingId}/`;
        }
        const response = await awaitTask(primerUrl, {
          method: 'POST',
          headers: this.headers,
        });

        if (response.task.status === 'complete') {
          await this.fetchEntity();
          alert('Successfully pulled prep from CRM');
        } else {
          throw new Error('Failed to generate primer');
        }
      } catch (error) {
        console.log('ERROR', error);
        Sentry.captureException(error);
        alert('Failed to generate primer');
      } finally {
        this.isGenerating = false;
      }
    },

    async updatePrimer() {
      const storedPrimer = JSON.stringify({
        summary: this.meetingPrimer?.summary,
        suggested_questions: this.meetingPrimer?.suggested_questions,
        financial_summary: this.meetingPrimer?.financial_summary,
      });
      const editedPrimer = JSON.stringify({
        summary: this.editedPrimer.summary,
        suggested_questions: this.editedPrimer.suggested_questions,
        financial_summary: this.editedPrimer.financial_summary,
      });

      if (storedPrimer === editedPrimer) {
        return;
      }

      this.isUpdatingPrimer = true;

      try {
        console.log('Saving primer changes:', this.meetingPrimer); // Debug log
        let primerUrl = '';
        if (this.entityExternalId) {
          primerUrl = `/api/v1/meeting-primer/${this.entityExternalId}/update/`;
        } else {
          primerUrl = `/api/v1/meeting-primer/${this.meetingId}/update-via-meeting/`;
        }
        const response = await fetch(primerUrl, {
          method: 'POST',
          headers: this.headers,
          body: editedPrimer,
        });

        if (!response.ok) {
          throw new Error('Failed to update primer');
        }

        const updatedPrimer = await response.json();
        // Update both the primer and editedPrimer
        await this.fetchEntity();
        this.editedPrimer = {
          summary: updatedPrimer.summary,
          suggested_questions: JSON.parse(JSON.stringify(updatedPrimer.suggested_questions)),
          financial_summary: JSON.parse(JSON.stringify(updatedPrimer.financial_summary)),
        };

        this.$buefy.toast.open({
          message: 'Primer updated successfully',
          type: 'is-success',
        });
      } catch (error) {
        Sentry.captureException(error);
        console.error('Error updating primer:', error);
        this.$oruga.notification.open({
          message: 'Failed to update primer',
          variant: 'danger',
        });
      } finally {
        this.isUpdatingPrimer = false;
      }
    },

    handleClickOutside(event) {
      const dropdown = event.target.closest('.dropdown');
      if (!dropdown) {
        this.isBrowseDropdownActive = false;
      }
    },
    copyTranscript() {
      const transcriptContent = this.$refs.transcriptContent.innerText;
      navigator.clipboard.writeText(transcriptContent).then(() => {
        alert('Transcript copied to clipboard');
      }).catch(err => {
        console.error('Failed to copy transcript content: ', err);
      });
    },
    handleMeetingsListModalClose() {
      this.showMeetingsListModal = false;
    },
    handleKeyInfoUpdate(updatedKeyInfo) {
      // Deep clone the updated key info to prevent reference issues
      this.editedMeeting.key_info = JSON.parse(JSON.stringify(updatedKeyInfo));
      // Since this is called when exiting edit mode, save the changes
      this.saveMeetingChanges();
    },

    cleanDateString(dateString) {
      try {
        return new Date(dateString).toISOString();
      } catch (error) {
        return null;
      }
    },

    async saveMeetingChanges() {
      const storedMeeting = JSON.stringify({
        title: this.meeting.title,
        meeting_date: this.cleanDateString(this.meeting.meeting_date),
        summary: this.meeting.summary,
        key_info: this.meeting.key_info,
        transcript: this.meeting.transcript,
      });
      const editedMeeting = JSON.stringify({
        title: this.editedMeeting.title,
        meeting_date: this.cleanDateString(this.editedMeeting.meeting_date),
        summary: this.editedMeeting.summary,
        key_info: this.editedMeeting.key_info,
        transcript: this.editedMeeting.transcript,
      });

      // No need to save since no changes were made
      if (storedMeeting === editedMeeting) {
        return;
      }

      this.isSavingEdits = true;

      try {
        console.log('Saving meeting changes:', this.editedMeeting); // Debug log
        const response = await fetch(`/api/v1/meetings/${this.meetingId}/update/`, {
          method: 'POST',
          headers: this.headers,
          body: editedMeeting,
        });

        if (!response.ok) {
          throw new Error('Failed to update meeting');
        }

        const updatedMeeting = await response.json();
        // Update both the meeting and editedMeeting
        this.meeting = { ...this.meeting, ...updatedMeeting };
        this.editedMeeting = {
          title: updatedMeeting.title,
          meeting_date: new Date(updatedMeeting.meeting_date).toISOString().slice(0, 10),
          summary: updatedMeeting.summary,
          key_info: JSON.parse(JSON.stringify(updatedMeeting.key_info)),
          transcript: updatedMeeting.transcript,
        };

        this.$buefy.toast.open({
          message: 'Meeting updated successfully',
          type: 'is-success',
        });
      } catch (error) {
        Sentry.captureException(error);
        console.error('Error updating meeting:', error);
        this.$oruga.notification.open({
          message: 'Failed to update meeting',
          variant: 'danger',
        });
      } finally {
        this.isSavingEdits = false;
      }
    },

    handlePrimerSummaryUpdate() {
      if (this.editingPrimerSummary) {
        this.updatePrimer();
      }
      this.editingPrimerSummary = !this.editingPrimerSummary;
    },

    handlePrimerQuestionsUpdate(updatedQuestions) {
      // Deep clone
      this.editedPrimer.suggested_questions = JSON.parse(JSON.stringify(updatedQuestions));
      this.updatePrimer();
    },

    handlePrimerFinancialUpdate(updatedFinancial) {
      // Deep clone
      this.editedPrimer.financial_summary = JSON.parse(JSON.stringify(updatedFinancial));
      this.updatePrimer();
    },

    handleSummaryEdit() {
      if (this.editingSummary) {
        this.saveMeetingChanges();
      }
      this.editingSummary = !this.editingSummary;
    },

    handleKeyInfoEdit() {
      this.editingKeyInfo = !this.editingKeyInfo;
    },

    handleTranscriptEdit() {
      if (this.editingTranscript) {
        this.saveMeetingChanges();
      }
      this.editingTranscript = !this.editingTranscript;
    },

    openAttendeeEditModal(attendee) {
      this.originalAttendeeName = attendee;
      this.editingAttendeeName = attendee;
      this.isAttendeeEditModalOpen = true;
    },

    async saveAttendeeName() {
      if (!this.editingAttendeeName.trim()) {
        return;
      }

      this.updatingAttendeeName = true;
      try {
        const response = await fetch(`/api/v1/meetings/${this.meeting.id}/update-attendee/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...this.headers,
          },
          body: JSON.stringify({
            old_name: this.originalAttendeeName,
            new_name: this.editingAttendeeName.trim(),
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to update attendee name');
        }

        const data = await response.json();
        this.meeting = data.meeting;
        this.isAttendeeEditModalOpen = false;

        this.$buefy.toast.open({
          message: 'Attendee name updated successfully',
          type: 'is-success',
        });
      } catch (error) {
        console.error('Error updating attendee name:', error);
        this.$buefy.toast.open({
          message: 'Failed to update attendee name',
          type: 'is-danger',
        });
      } finally {
        this.updatingAttendeeName = false;
      }
    },

    async pushSummaryToCRM() {
      try {
        const response = await fetchWrapper(`/api/v1/crm/${this.selectedCrm.toLowerCase()}/meeting-summary/`, {
          method: 'POST',
          headers: this.headers,
          body: JSON.stringify({
            contactId: this.meeting.crm_connection_id,
            meetingId: this.meeting.id,
            meetingSummary: {
              title: this.meeting.title || 'Meeting',
              date: this.meeting.meeting_date,
              summary: this.meeting.summary,
              keyInfo: this.meeting.key_info || [],
              attendees: this.meeting.attendees || [],
              url: window.location.href,
            },
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to push summary to CRM');
        }
        await this.fetchMeeting();

        alert('Meeting summary pushed to CRM successfully');
      } catch (error) {
        console.error('Error pushing summary to CRM:', error);
        alert('Failed to push summary to CRM');

        Sentry.captureException(error);
      } finally {
        this.showPushSummaryDropdown = false;
      }
    },

    async reprocessMeeting(meetingType) {
      if (!this.meeting || this.isReprocessing) return;

      this.isReprocessing = true;
      this.showMeetingTypeDropdown = false;

      try {
        const response = await awaitTask(`/api/v1/meetings/${this.meeting.id}/reprocess/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.$root.$data.csrfToken,
          },
          body: JSON.stringify({ meeting_type: meetingType }),
        });

        if (response.task.status === 'complete') {
          // Fetch the latest meeting data
          await this.fetchMeetingAndInitializeTasks();

          this.$oruga.notification.open({
            message: 'Meeting reprocessed successfully',
            variant: 'success',
          });
        } else {
          throw new Error('Failed to reprocess meeting');
        }
      } catch (error) {
        console.error('Error reprocessing meeting:', error);
        this.$oruga.notification.open({
          message: 'Failed to reprocess meeting',
          variant: 'danger',
        });
      } finally {
        this.isReprocessing = false;
      }
    },
    async copyMarkdownAsRichText() {
      // Convert Markdown to HTML
      const html = marked(this.meeting.followup_email);

      // Create a Blob with HTML content
      const blob = new Blob([html], { type: 'text/html' });
      const data = [new ClipboardItem({ 'text/html': blob })];

      try {
        // Write to clipboard
        await navigator.clipboard.write(data);
        console.log('Copied as rich text!');
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    },
    renderMarkdown(text) {
      if (!text) return '';
      // First convert markdown to HTML, then sanitize the output
      const htmlContent = marked(text);
      return DOMPurify.sanitize(htmlContent);
    },

    async handleReprocessSummary() {
      // Always show the modal with the current prompt (default or custom)
      await this.loadCustomPrompts();
      this.customPromptModalSection = 'summary';
      this.editedPromptText = '';
      this.isEditingPrompt = false;
      this.showCustomPromptModal = true;
    },

    async handleReprocessKeyInfo() {
      // Always show the modal with the current prompt (default or custom)
      await this.loadCustomPrompts();
      this.customPromptModalSection = 'key_info';
      this.editedPromptText = '';
      this.isEditingPrompt = false;
      this.showCustomPromptModal = true;
    },

    async handleReprocessEmail() {
      // Always show the modal with the current prompt (default or custom)
      await this.loadCustomPrompts();
      this.customPromptModalSection = 'followup_email';
      this.editedPromptText = '';
      this.isEditingPrompt = false;
      this.showCustomPromptModal = true;
    },

    async handleReprocessTasks() {
      // Always show the modal with the current prompt (default or custom)
      await this.loadCustomPrompts();
      this.customPromptModalSection = 'tasks';
      this.editedPromptText = '';
      this.isEditingPrompt = false;
      this.showCustomPromptModal = true;
    },

    async loadCustomPrompts() {
      try {
        const response = await fetch('/api/v1/custom-prompts/', {
          method: 'GET',
          headers: this.headers,
        });
        const data = await response.json();
        this.availableCustomPrompts = data;

        // Ensure we have entries for all sections, including default prompts
        if (this.meeting?.meeting_type && !this.availableCustomPrompts[this.meeting.meeting_type]) {
          this.availableCustomPrompts[this.meeting.meeting_type] = {};
        }

        // If there's no custom prompt for a section, create an entry with default prompt
        const sections = ['summary', 'key_info', 'email', 'crm_tasks'];
        const sectionMap = {
          summary: 'summary',
          key_info: 'key_info',
          email: 'followup_email',
          crm_tasks: 'tasks',
        };

        sections.forEach(apiSection => {
          const localSection = sectionMap[apiSection];
          if (this.meeting?.meeting_type && !this.availableCustomPrompts[this.meeting.meeting_type][localSection]) {
            this.availableCustomPrompts[this.meeting.meeting_type][localSection] = {
              text: this.getDefaultPromptText(localSection),
              is_custom: false,
            };
          }
        });
      } catch (error) {
        console.error('Error loading custom prompts:', error);
        this.availableCustomPrompts = {};
      }
    },

    async reprocessWithPrompt(section, customPrompt) {
      const isProcessingSummary = section === 'summary';
      const isProcessingKeyInfo = section === 'key_info';
      const isProcessingEmail = section === 'followup_email';
      const isProcessingTasks = section === 'tasks';

      if (isProcessingSummary) {
        this.isReprocessingSummary = true;
      } else if (isProcessingKeyInfo) {
        this.isReprocessingKeyInfo = true;
      } else if (isProcessingEmail) {
        this.isReprocessingEmail = true;
      } else if (isProcessingTasks) {
        this.isReprocessingTasks = true;
      }

      try {
        let endpoint;
        if (section === 'summary') {
          endpoint = `/api/v1/meetings/${this.meeting.id}/reprocess/summary/`;
        } else if (section === 'key_info') {
          endpoint = `/api/v1/meetings/${this.meeting.id}/reprocess/key-info/`;
        } else if (section === 'followup_email') {
          endpoint = `/api/v1/meetings/${this.meeting.id}/reprocess/followup-email/`;
        } else if (section === 'tasks') {
          endpoint = `/api/v1/meetings/${this.meeting.id}/reprocess/tasks/`;
        }

        const requestBody = customPrompt ? { custom_prompt: customPrompt } : {};

        const response = await awaitTask(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.$root.$data.csrfToken,
          },
          body: JSON.stringify(requestBody),
        });

        if (response.task.status === 'complete') {
          // Refresh meeting data
          await this.fetchMeeting();

          this.$buefy.toast.open({
            message: `${section === 'summary' ? 'Summary' : 'Key information'} reprocessed successfully`,
            type: 'is-success',
          });
        } else {
          throw new Error(`${section} reprocessing failed`);
        }
      } catch (error) {
        console.error(`Error reprocessing ${section}:`, error);
        this.$buefy.toast.open({
          message: `Failed to reprocess ${section}`,
          type: 'is-danger',
        });
      } finally {
        if (isProcessingSummary) {
          this.isReprocessingSummary = false;
        } else if (isProcessingKeyInfo) {
          this.isReprocessingKeyInfo = false;
        } else if (isProcessingEmail) {
          this.isReprocessingEmail = false;
        } else if (isProcessingTasks) {
          this.isReprocessingTasks = false;
        }
      }
    },


    async confirmCustomPromptReprocess() {
      this.showCustomPromptModal = false;

      // Use the current prompt (either default or custom)
      const promptText = this.currentPromptText;

      await this.reprocessWithPrompt(this.customPromptModalSection, promptText);
    },

    cancelCustomPromptReprocess() {
      this.showCustomPromptModal = false;
      this.customPromptModalSection = '';
      this.editedPromptText = '';
      this.isEditingPrompt = false;
    },

    toggleEditPrompt() {
      if (this.isEditingPrompt) {
        this.cancelEditPrompt();
      } else {
        this.isEditingPrompt = true;
        this.editedPromptText = this.currentPromptText;
      }
    },

    cancelEditPrompt() {
      this.isEditingPrompt = false;
      this.editedPromptText = '';
    },

    async savePrompt() {
      if (!this.editedPromptText.trim()) {
        alert('Prompt text cannot be empty');
        return;
      }

      this.savingPrompt = true;

      // Map local section names to API section names
      const sectionToApiMap = {
        summary: 'summary',
        key_info: 'key_info',
        followup_email: 'email',
        tasks: 'crm_tasks',
      };

      const apiSection = sectionToApiMap[this.customPromptModalSection];

      try {
        const response = await fetch('/api/v1/custom-prompts/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.$root.$data.csrfToken,
          },
          body: JSON.stringify({
            meeting_type: this.meeting.meeting_type,
            prompt_type: apiSection,
            prompt_text: this.editedPromptText,
          }),
        });

        const data = await response.json();

        if (data.success) {
          // Update local state to reflect the new custom prompt
          if (!this.availableCustomPrompts[this.meeting.meeting_type]) {
            this.availableCustomPrompts[this.meeting.meeting_type] = {};
          }
          this.availableCustomPrompts[this.meeting.meeting_type][this.customPromptModalSection] = {
            text: this.editedPromptText,
            is_custom: true,
          };

          // Exit edit mode
          this.isEditingPrompt = false;
          this.editedPromptText = '';

          this.$buefy.toast.open({
            message: 'Custom prompt saved successfully!',
            type: 'is-success',
          });
        } else {
          throw new Error(data.error || 'Failed to save custom prompt');
        }
      } catch (error) {
        console.error('Error saving custom prompt:', error);
        this.$buefy.toast.open({
          message: 'Failed to save custom prompt',
          type: 'is-danger',
        });
      } finally {
        this.savingPrompt = false;
      }
    },

    async revertToDefaultPrompt() {
      if (!confirm('Are you sure you want to revert to the default prompt? This will delete your custom prompt.')) {
        return;
      }

      // Map local section names to API section names
      const sectionToApiMap = {
        summary: 'summary',
        key_info: 'key_info',
        followup_email: 'email',
        tasks: 'crm_tasks',
      };

      const apiSection = sectionToApiMap[this.customPromptModalSection];

      try {
        const response = await fetch('/api/v1/custom-prompts/delete/', {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.$root.$data.csrfToken,
          },
          body: JSON.stringify({
            meeting_type: this.meeting.meeting_type,
            prompt_type: apiSection,
          }),
        });

        const data = await response.json();

        if (data.success) {
          // Reload prompts to get the default prompt back
          await this.loadCustomPrompts();
          this.$buefy.toast.open({
            message: 'Reverted to default prompt successfully!',
            type: 'is-success',
          });
        } else {
          throw new Error(data.error || 'Failed to revert prompt');
        }
      } catch (error) {
        console.error('Error reverting prompt:', error);
        this.$buefy.toast.open({
          message: 'Failed to revert prompt',
          type: 'is-danger',
        });
      }
    },

    getSectionTitle(section) {
      const titles = {
        summary: 'Summary',
        key_info: 'Key Information',
        followup_email: 'Follow-up Email',
        tasks: 'Tasks',
      };
      return titles[section] || section;
    },

    getDefaultPromptText(promptType) {
      // Default prompts based on meeting type and section
      const defaultPrompts = {
        summary: 'You are a helpful assistant that creates concise meeting summaries. Provide a clear, structured summary that captures the main points, decisions made, and outcomes discussed in the meeting.',
        key_info: 'You are a helpful assistant that extracts key information from meeting transcripts. Extract important details such as decisions made, key discussion points, important dates, numbers, and any other significant information mentioned.',
        followup_email: 'You are a helpful assistant that drafts follow-up emails based on meeting transcripts. Create a professional email that summarizes the meeting and includes any next steps or action items. At the end of the email, you can include action items for each meeting participant. Return the email purely as formatted text as simple Markdown.',
        tasks: 'You are a virtual assistant that identifies tasks and action items from meeting transcripts. These tasks will be pushed to a task management system to track follow-ups, assignments, and responsibilities. Focus on actionable tasks that require follow-up action. Return the response as a JSON array of task objects with properties like "name", "description", "assignee", and "due_date".',
      };

      return defaultPrompts[promptType] || '';
    },

    async checkCanEdit() {
      if (!this.meeting?.external_id) return false;

      if(await this.advisorStore.can('edit', 'meeting', this.meeting.external_id)) {
        return true;
      }

      if (!this.meeting?.entity?.external_id) return false;
      return await this.advisorStore.can('edit', 'entity', this.meeting.entity.external_id);
    }
  },
};
</script>

<style scoped>
.has-text-weight-extra-bold {
  font-weight: 900 !important;
}

.task-editor-modal ::v-deep .modal-content {
  border-radius: 1rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
}

.task-editor-modal ::v-deep .field-label {
  font-weight: 600;
  color: #4a5568;
}

.task-editor-modal ::v-deep .input,
.task-editor-modal ::v-deep .select select {
  border-color: #e2e8f0;
  border-radius: 0.5rem;
  transition: border-color 0.2s ease;
}

.task-editor-modal ::v-deep .input:focus,
.task-editor-modal ::v-deep .select select:focus {
  border-color: #4299e1;
  box-shadow: 0 0 0 1px #4299e1;
}

.task-editor-modal ::v-deep .button {
  font-weight: 600;
  transition: all 0.2s ease;
}

.custom-tabs-container {
  display: inline-flex;
  background-color: #f3f4f6;
  padding: 0.25rem;
  border-radius: 9999px;
}

.grid-container {
  display: grid;
  grid-template-columns: 1fr 20rem;
  column-gap: var(--stk-size-8)
}

#header {
  grid-row: 1;
  grid-column: 1/3;
}

#type {
  grid-row: 2;
  grid-column: 1 / span 2;
  display: flex;
  align-items: center;
  gap: 1em;

  :deep(.stk-callout-body) {
    display: flex;
    align-items: center;
    gap: 1em;
  }
}

#main-content {
  grid-row: 3;
  grid-column: 1;
}

#sidebar {
  grid-row: 3;
  grid-column: 2;
}

@media (max-width: 768px) {
  .grid-container {
    grid-template-columns: 1fr;
  }

  .sidebar {
    margin-top: 1rem;
  }
}

.button.o-btn--outlined {
  background-color: white;
  color: #363636;
  border-color: #dbdbdb;
}

.button.o-btn--outlined:hover:not(:disabled) {
  background-color: #f5f5f5;
  border-color: #b5b5b5;
}
.has-text-weight-extra-bold {
  font-weight: 900 !important;
}

.task-editor-modal ::v-deep .modal-content {
  border-radius: 1rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
}

.task-editor-modal ::v-deep .field-label {
  font-weight: 600;
  color: #4a5568;
}

.task-editor-modal ::v-deep .input,
.task-editor-modal ::v-deep .select select {
  border-color: #e2e8f0;
  border-radius: 0.5rem;
  transition: border-color 0.2s ease;
}

.task-editor-modal ::v-deep .input:focus,
.task-editor-modal ::v-deep .select select:focus {
  border-color: #4299e1;
  box-shadow: 0 0 0 1px #4299e1;
}

.task-editor-modal ::v-deep .button {
  font-weight: 600;
  transition: all 0.2s ease;
}

.tab-label {
  cursor: inherit;
}

/* Style for disabled tabs */
:deep(.o-tabs-item.is-disabled) {
  opacity: 0.6;
  cursor: not-allowed;
}

:deep(.o-tabs-item.is-disabled:hover) {
  cursor: not-allowed;
}

.textarea {
  width: 100%;
  font-family: inherit;
  font-size: inherit;
  padding: 0.75rem;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  transition: border-color 0.2s ease;
}

.textarea:focus {
  border-color: #485fc7;
  box-shadow: 0 0 0 0.125em rgba(72, 95, 199, 0.25);
}

.attendees-section {
  background-color: #f5f5f5;
  border-radius: 4px;
}

.tag .button.is-ghost {
  padding: 0;
  height: auto;
}

.tag .button.is-ghost:hover {
  background: none;
  opacity: 0.7;
}
.dropdown-item {
  text-align: left !important;
}

.transparent-input {
  background-color: transparent;
  border-radius: 0.25em;
  cursor: pointer;
  transition-duration: var(--stk-transition-duration);
  transition-property: background-color;

  &::placeholder {
    color: var(--stk-text-weak);
  }

  &:hover {
    background-color: var(--stk-background-alt);
  }

  &:focus-visible {
    outline-color: var(--stk-primary);
    outline-offset: 2px;
  }
}
</style>
